/**
 * /formations — Formations IA pour avocats par Pierre-Louis
 */

import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Trans, useTranslation } from 'react-i18next';
import { PublicHeader, PublicFooter } from '@/components/jurisprudence';

const SITE_URL = 'https://smartlawyer.ai';

// Textes : formations.items.<id>.{title,level,duration,desc,topics.t1..t5}
const TOPIC_KEYS = ['t1', 't2', 't3', 't4', 't5'];

const FORMATIONS = [
  {
    id:       'daily',
    color:    'border-blue-200 bg-blue-50/50 dark:border-blue-800 dark:bg-blue-950/20',
    badge:    'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  },
  {
    id:       'advanced',
    color:    'border-primary/20 bg-primary/5',
    badge:    'bg-primary/10 text-primary',
  },
  {
    id:       'gdpr',
    color:    'border-amber-200 bg-amber-50/50 dark:border-amber-800 dark:bg-amber-950/20',
    badge:    'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  },
  {
    id:       'custom',
    color:    'border-emerald-200 bg-emerald-50/50 dark:border-emerald-800 dark:bg-emerald-950/20',
    badge:    'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  },
];

// Textes : formations.credentials.<id>
const CREDENTIALS = ['bar', 'specialist', 'certified', 'senate', 'trilingual', 'trainer'];

interface FormData {
  nom:        string;
  email:      string;
  cabinet:    string;
  telephone:  string;
  formation:  string;
  message:    string;
}

export default function Formations() {
  const { t } = useTranslation();
  const [form, setForm]       = useState<FormData>({
    nom: '', email: '', cabinet: '', telephone: '', formation: '', message: '',
  });
  const [sending, setSending] = useState(false);
  const [sent,    setSent]    = useState(false);
  const [error,   setError]   = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSending(true);
    try {
      const res = await fetch('/api/formations/contact', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify(form),
      });
      if (!res.ok) throw new Error('Erreur serveur');
      setSent(true);
    } catch {
      setError(t('formations.form.error'));
    } finally {
      setSending(false);
    }
  };

  const inputCls = "w-full rounded-lg border border-input bg-background px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring placeholder:text-muted-foreground";

  return (
    <>
      <Helmet>
        <title>{t('formations.meta_title')}</title>
        <meta name="description"
          content={t('formations.meta_description')} />
        <link rel="canonical" href={`${SITE_URL}/formations`} />
      </Helmet>

      <div className="min-h-screen bg-background flex flex-col">
        <PublicHeader />

        <main className="flex-1">

          {/* Hero */}
          <section className="py-20 border-b">
            <div className="max-w-5xl mx-auto px-4 sm:px-6">
              <div className="grid sm:grid-cols-2 gap-12 items-center">
                <div>
                  <div className="inline-flex items-center gap-2 text-xs font-medium text-primary bg-primary/10 border border-primary/20 px-3 py-1.5 rounded-full mb-6">
                    {t('formations.hero.badge')}
                  </div>
                  <h1 className="text-4xl sm:text-5xl font-bold leading-tight tracking-tight mb-6">
                    {t('formations.hero.title_start')}{' '}
                    <span className="text-primary">{t('formations.hero.title_highlight')}</span>
                  </h1>
                  <p className="text-lg text-muted-foreground leading-relaxed mb-8">
                    {t('formations.hero.desc')}
                  </p>
                  <a href="#inscription"
                    className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-primary text-primary-foreground font-bold hover:bg-primary/90 transition-colors shadow-lg shadow-primary/20">
                    {t('formations.request_program')}
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" transform="rotate(-90 12 12)" />
                    </svg>
                  </a>
                </div>

                {/* Photo Pierre-Louis */}
                <div className="flex justify-center">
                  <div className="relative">
                    <div className="w-72 h-72 rounded-2xl overflow-hidden shadow-2xl border-4 border-background">
                      <img
                        src="/public/pl.jpeg"
                        alt={t('formations.hero.photo_alt')}
                        className="w-full h-full object-cover"
                        onError={e => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    </div>
                    <div className="absolute -bottom-4 -right-4 bg-card border rounded-xl px-4 py-3 shadow-lg">
                      <p className="text-xs font-semibold text-foreground">Pierre-Louis</p>
                      <p className="text-xs text-muted-foreground">{t('formations.credentials.bar')}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Profil */}
          <section className="py-16 border-b bg-muted/20">
            <div className="max-w-4xl mx-auto px-4 sm:px-6">
              <h2 className="text-2xl font-bold mb-8">{t('formations.trainer.title')}</h2>
              <div className="grid sm:grid-cols-2 gap-8 items-start">
                <div className="space-y-4 text-muted-foreground leading-relaxed">
                  <p>
                    <Trans
                      i18nKey="formations.trainer.p1"
                      components={{ strong: <strong className="text-foreground" /> }}
                    />
                  </p>
                  <p>
                    {t('formations.trainer.p2')}
                  </p>
                  <p>
                    {t('formations.trainer.p3')}
                  </p>
                </div>
                <div className="space-y-2.5">
                  {CREDENTIALS.map(c => (
                    <div key={c} className="flex items-center gap-3 text-sm">
                      <svg className="w-4 h-4 text-primary flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-foreground">{t(`formations.credentials.${c}`)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Formations */}
          <section className="py-16 border-b">
            <div className="max-w-4xl mx-auto px-4 sm:px-6">
              <h2 className="text-2xl font-bold mb-2">{t('formations.list.title')}</h2>
              <p className="text-muted-foreground mb-10">
                {t('formations.list.desc')}
              </p>
              <div className="space-y-5">
                {FORMATIONS.map(f => (
                  <div key={f.id} className={`rounded-xl border p-6 ${f.color}`}>
                    <div className="flex items-start justify-between gap-4 mb-3">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${f.badge}`}>
                            {t(`formations.items.${f.id}.level`)}
                          </span>
                          <span className="text-xs text-muted-foreground">· {t(`formations.items.${f.id}.duration`)}</span>
                        </div>
                        <h3 className="text-base font-bold">{t(`formations.items.${f.id}.title`)}</h3>
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground mb-4 leading-relaxed">{t(`formations.items.${f.id}.desc`)}</p>
                    <div className="flex flex-wrap gap-2">
                      {TOPIC_KEYS.map(k => (
                        <span key={k} className="text-xs bg-background/80 border rounded-full px-2.5 py-1 text-muted-foreground">
                          {t(`formations.items.${f.id}.topics.${k}`)}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Formulaire */}
          <section id="inscription" className="py-16">
            <div className="max-w-2xl mx-auto px-4 sm:px-6">
              <h2 className="text-2xl font-bold mb-2">{t('formations.request_program')}</h2>
              <p className="text-muted-foreground mb-8">
                {t('formations.form.intro')}
              </p>

              {sent ? (
                <div className="rounded-xl border bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800 p-8 text-center">
                  <div className="text-4xl mb-4">✓</div>
                  <p className="font-semibold text-green-700 dark:text-green-400 mb-1">
                    {t('formations.form.sent_title')}
                  </p>
                  <p className="text-sm text-green-600 dark:text-green-500">
                    {t('formations.form.sent_desc')}
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-medium text-muted-foreground mb-1.5 block">
                        {t('formations.form.name')}
                      </label>
                      <input
                        type="text" required placeholder={t('formations.form.name_placeholder')}
                        value={form.nom}
                        onChange={e => setForm(f => ({ ...f, nom: e.target.value }))}
                        className={inputCls}
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-muted-foreground mb-1.5 block">
                        {t('formations.form.email')}
                      </label>
                      <input
                        type="email" required placeholder={t('formations.form.email_placeholder')}
                        value={form.email}
                        onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                        className={inputCls}
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-medium text-muted-foreground mb-1.5 block">
                        {t('formations.form.firm')}
                      </label>
                      <input
                        type="text" placeholder={t('formations.form.firm_placeholder')}
                        value={form.cabinet}
                        onChange={e => setForm(f => ({ ...f, cabinet: e.target.value }))}
                        className={inputCls}
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-muted-foreground mb-1.5 block">
                        {t('formations.form.phone')}
                      </label>
                      <input
                        type="tel" placeholder={t('formations.form.phone_placeholder')}
                        value={form.telephone}
                        onChange={e => setForm(f => ({ ...f, telephone: e.target.value }))}
                        className={inputCls}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-xs font-medium text-muted-foreground mb-1.5 block">
                      {t('formations.form.training')}
                    </label>
                    <select
                      value={form.formation}
                      onChange={e => setForm(f => ({ ...f, formation: e.target.value }))}
                      className={inputCls}
                    >
                      <option value="">{t('formations.form.select_training')}</option>
                      {FORMATIONS.map(f => (
                        <option key={f.id} value={t(`formations.items.${f.id}.title`, { lng: 'fr' })}>
                          {t(`formations.items.${f.id}.title`)} ({t(`formations.items.${f.id}.duration`)})
                        </option>
                      ))}
                      <option value="Autre">{t('formations.form.other')}</option>
                    </select>
                  </div>

                  <div>
                    <label className="text-xs font-medium text-muted-foreground mb-1.5 block">
                      {t('formations.form.message')}
                    </label>
                    <textarea
                      rows={4}
                      placeholder={t('formations.form.message_placeholder')}
                      value={form.message}
                      onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
                      className={inputCls + ' resize-none'}
                    />
                  </div>

                  {error && (
                    <p className="text-sm text-destructive">{error}</p>
                  )}

                  <button
                    type="submit"
                    disabled={sending}
                    className="w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-primary text-primary-foreground font-bold hover:bg-primary/90 disabled:opacity-50 transition-colors shadow-lg shadow-primary/20"
                  >
                    {sending ? (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      t('formations.form.submit')
                    )}
                  </button>

                  <p className="text-center text-xs text-muted-foreground">
                    {t('formations.form.direct_contact')}{' '}
                    <a href="mailto:pierrelouis@smartlawyer.ai" className="text-primary hover:underline">
                      pierrelouis@smartlawyer.ai
                    </a>
                  </p>
                </form>
              )}
            </div>
          </section>

        </main>

        <PublicFooter />
      </div>
    </>
  );
}